import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useAuth } from "context/AuthContextProvider";
import { getBusinessSettingLog, getBusinessSettings } from "hooks/api/business-settings";
import { BusinessSettingLogPage } from "shared/types";
import { isSystemAdmin } from "shared/utils/role";

// `enabled` exists for useAdminCapabilities (Managers resolve without a
// request). Independently of it, a SYSTEM ADMIN never issues this query: their
// token carries no business_id and the v2 /business mount is admin-only, so
// the request answers 401 and the axios interceptor signs them out. It did,
// from Bisnis -> Akun -> an account's detail page (InstructorDetails reads the
// settings to decide whether its Manajemen tab shows), 2026-09-03. The pages
// that read settings are business-scoped features, so for a system admin the
// value is simply undefined and those features stay hidden.
export const useBusinessSettings = (enabled = true) => {
  const { state } = useAuth();
  const sysAdmin = isSystemAdmin(state.user?.roles);
  return useQuery({
    queryKey: ["business", "settings"],
    queryFn: getBusinessSettings,
    enabled: enabled && !sysAdmin,
  });
};

// One page of the change log for a set of settings keys. Under the
// ["business"] prefix on purpose: the settings mutation invalidates that
// prefix, so a save refreshes the history beneath it. The previous page
// stays on screen while the next loads (no flash to a skeleton on a page
// flip). No retry: an older server answers 404 and the section simply hides.
export const useBusinessSettingLog = (fields: string[], page: number, pageSize: number) => {
  const { state } = useAuth();
  const sysAdmin = isSystemAdmin(state.user?.roles);
  return useQuery<BusinessSettingLogPage, Error>({
    queryKey: ["business", "settings-log", fields, page, pageSize],
    queryFn: () => getBusinessSettingLog({ fields, page, pageSize }),
    placeholderData: keepPreviousData,
    enabled: !sysAdmin,
    retry: false,
  });
};
